import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../../_css/Login.module.css';
import { notifyError, notifySuccess } from '../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { loginUser, getUserInfo, setAgency, microsoftLogin } from '../../requests';
import { saveAuthToken } from '../../utils/helper';
import { useDispatch } from 'react-redux';
import { setUserInformation } from '../../_store/reducers/userReducer';
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import getRole from '../../hooks/getRole';
import SetNewPassword from './SetNewPassword';
import ForgotPassword from './ForgotPassword';
import SsoOnboarding from './SsoOnboarding';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, microsoftLoginRedirectRequest, isMsalConfigured } from '../../authConfig';

const MS_LOGIN_INITIATED_KEY = 'ms_login_initiated';
const MS_PENDING_KEY = 'ms_login_pending';
const MS_PENDING_USER_EMAIL = 'ms_pending_user_email';
const MS_PENDING_AUTH_TOKEN = 'ms_pending_auth_token';
const MS_REQUEST_SENT_KEY = 'ms_request_sent';
const LS_PENDING_USER_EMAIL = 'ms_pending_user_email_ls';
const LS_PENDING_AUTH_TOKEN = 'ms_pending_auth_token_ls';

const getAccountEmail = (account) =>
  account?.username ||
  account?.idTokenClaims?.preferred_username ||
  account?.idTokenClaims?.email ||
  "";

const getRequestSentStorageKey = (email) =>
  email ? `${MS_REQUEST_SENT_KEY}_${email.toLowerCase()}` : MS_REQUEST_SENT_KEY;

const getStoredPendingEmail = () =>
  sessionStorage.getItem(MS_PENDING_USER_EMAIL) ||
  localStorage.getItem(LS_PENDING_USER_EMAIL);

const getStoredPendingAuthToken = () =>
  sessionStorage.getItem(MS_PENDING_AUTH_TOKEN) ||
  localStorage.getItem(LS_PENDING_AUTH_TOKEN);

const isRequestSentForEmail = (email) => {
  if (typeof window === "undefined" || !email) return false;
  const key = getRequestSentStorageKey(email);
  return (
    localStorage.getItem(key) === "1" || sessionStorage.getItem(key) === "1"
  );
};

const markRequestSent = (email) => {
  if (!email || typeof window === "undefined") return;
  const normalizedEmail = email.toLowerCase();
  const key = getRequestSentStorageKey(normalizedEmail);
  localStorage.setItem(key, "1");
  sessionStorage.setItem(key, "1");
  localStorage.setItem(LS_PENDING_USER_EMAIL, normalizedEmail);
  sessionStorage.setItem(MS_PENDING_USER_EMAIL, normalizedEmail);
  sessionStorage.setItem(MS_PENDING_KEY, "1");
};

const clearPendingStorage = (email) => {
  sessionStorage.removeItem(MS_LOGIN_INITIATED_KEY);
  sessionStorage.removeItem(MS_PENDING_KEY);
  sessionStorage.removeItem(MS_PENDING_USER_EMAIL);
  sessionStorage.removeItem(MS_PENDING_AUTH_TOKEN);
  sessionStorage.removeItem(MS_REQUEST_SENT_KEY);
  localStorage.removeItem(LS_PENDING_USER_EMAIL);
  localStorage.removeItem(LS_PENDING_AUTH_TOKEN);
  if (email) {
    const key = getRequestSentStorageKey(email);
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
};

const emailsMatch = (a, b) =>
  Boolean(a && b && a.toLowerCase() === b.toLowerCase());

const Login = () => {
  const role = getRole();
  const [token, setToken] = useState(null);
  
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [activeTab, setActiveTab] = useState('super-admin');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo.png`;

  const navigate = useNavigate();

  const { instance, accounts, inProgress } = useMsal();
  const msLoginHandledRef = useRef(false);
  const isMsReady = inProgress === InteractionStatus.None;
  const [pendingApproval, setPendingApproval] = useState(() => {
    if (typeof window === "undefined") return false;
    if (sessionStorage.getItem(MS_PENDING_KEY) === "1") return true;
    const email = getStoredPendingEmail();
    return email ? isRequestSentForEmail(email) : false;
  });
  const [pendingUserEmail, setPendingUserEmail] = useState(() =>
    typeof window !== "undefined" ? getStoredPendingEmail() : null
  );
  const [pendingAuthToken, setPendingAuthToken] = useState(() =>
    typeof window !== "undefined" ? getStoredPendingAuthToken() : null
  );
  const [requestSent, setRequestSent] = useState(() =>
    isRequestSentForEmail(getStoredPendingEmail())
  );

  const validateForm = () => {
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Please enter your email';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email';
      notifyError('Please enter a valid email');
    }

    if (!password) {
      newErrors.password = 'Please enter your password';
    } else if (password.length < 8) {
      newErrors.password = 'Your password must be at least 8 characters';
      notifyError('Your password must be at least 8 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetFields = () => {
    setEmail('');
    setPassword('');
    setErrors({});
  };

  const getUserInfoData = async (skip = false) => {
    if (skip) {
      return;
    }


    try {
      const response = await getUserInfo({ isSuperAdmin: false });
      if (response?.status === 200) {
        console.log(response?.data);
        const { agency } = response?.data;
        if (agency) {
          return agency;
        }
        return null;

      }
    } catch (error) {
      console.log(error);


    }
  }


  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const isAdmin = activeTab === "super-admin";

    dispatch(startLoader());

    try {
      const response = await loginUser({ email, password, isSuperAdmin: isAdmin });

      if (response?.status < 400) {
        const { message, user, token } = response?.data;
        const { full_name, id, email, isActive, agency_id, invited_by_id ,role} = user || {};


        let isSuperAdmin = false;

        if(role === "super_admin"){
          isSuperAdmin = true;
          
        } else {
          isSuperAdmin = false;
        }


        saveAuthToken(JSON.stringify(token));


        const agency = await getUserInfoData(activeTab === "super-admin");


        dispatch(
          setUserInformation(
            full_name || "",
            id || "",
            email || "",
            isActive || 0,
            isSuperAdmin || false,
            agency_id || "",
            invited_by_id || "",
            agency?.name || "",
            agency?.email || "",
            role || ""
          )
        );
        
        resetFields();
        notifySuccess(message || "Login successful");

        
      }

    } catch (error) {
      console.log(error);
      notifyError(error?.response?.data?.message || 'Something went wrong');
    }
    finally {
      dispatch(stopLoader());
    }
  };

  // Exchange the signed-in Microsoft account for an app session and log in.
  const completeMicrosoftLogin = useCallback(
    async (account) => {
      dispatch(startLoader());
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        const accessToken = tokenResponse.accessToken;

        const response = await microsoftLogin({ accessToken });
        const data = response?.data || {};

        // Account exists but is not active yet.
        // Backend tells us if admin request is already sent, so frontend stays simple.
        if (data?.status === "pending") {
          sessionStorage.removeItem(MS_LOGIN_INITIATED_KEY);
          sessionStorage.setItem(MS_PENDING_KEY, "1");
          const userEmail =
            data?.user?.email || getAccountEmail(account);
          if (userEmail) {
            const normalizedEmail = userEmail.toLowerCase();
            sessionStorage.setItem(MS_PENDING_USER_EMAIL, normalizedEmail);
            localStorage.setItem(LS_PENDING_USER_EMAIL, normalizedEmail);
            setPendingUserEmail(normalizedEmail);
          }
          if (data?.token) {
            const tokenValue = JSON.stringify(data.token);
            sessionStorage.setItem(MS_PENDING_AUTH_TOKEN, tokenValue);
            localStorage.setItem(LS_PENDING_AUTH_TOKEN, tokenValue);
            setPendingAuthToken(tokenValue);
          }
          setRequestSent(Boolean(data?.hasRequestedAdmin));
          setPendingApproval(true);
          return;
        }

        if (response?.status < 400 && data?.token) {
          const { message, user, token } = data;
          const { full_name, id, email, isActive, agency_id, invited_by_id, role } =
            user || {};

          const isSuperAdmin = role === "super_admin";

          saveAuthToken(JSON.stringify(token));

          const agency = await getUserInfoData(isSuperAdmin);

          dispatch(
            setUserInformation(
              full_name || "",
              id || "",
              email || "",
              isActive || 0,
              isSuperAdmin || false,
              agency_id || "",
              invited_by_id || "",
              agency?.name || "",
              agency?.email || "",
              role || ""
            )
          );

          clearPendingStorage(email);
          setPendingUserEmail(null);
          setPendingAuthToken(null);
          setPendingApproval(false);
          setRequestSent(false);
          notifySuccess(message || "Login successful");
        }
      } finally {
        dispatch(stopLoader());
      }
    },
    // getUserInfoData / setUserInformation are stable enough for this flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, dispatch]
  );

  // After Microsoft redirect, refresh, or browser reopen — resume pending flow.
  useEffect(() => {
    if (!isMsReady || msLoginHandledRef.current) return;

    const account = accounts[0];
    if (!account) return;

    const accountEmail = getAccountEmail(account);
    const initiated = sessionStorage.getItem(MS_LOGIN_INITIATED_KEY) === "1";
    const pending = sessionStorage.getItem(MS_PENDING_KEY) === "1";
    const storedPendingEmail = getStoredPendingEmail();
    const requestAlreadySent = isRequestSentForEmail(
      accountEmail || storedPendingEmail
    );
    const samePendingUser = emailsMatch(accountEmail, storedPendingEmail);

    const shouldVerify =
      initiated || pending || (requestAlreadySent && samePendingUser);

    if (!shouldVerify) return;

    msLoginHandledRef.current = true;
    completeMicrosoftLogin(account).catch((err) => {
      notifyError(
        err?.response?.data?.message ||
          err?.message ||
          "Microsoft sign-in failed"
      );
      sessionStorage.removeItem(MS_LOGIN_INITIATED_KEY);
      msLoginHandledRef.current = false;
    });
  }, [isMsReady, accounts, completeMicrosoftLogin]);

  const onMicrosoftLogin = async () => {
    if (!isMsReady) return;
    if (!isMsalConfigured) {
      notifyError(
        "Microsoft sign-in is not configured. Set REACT_APP_MSAL_CLIENT_ID in .env.development and restart the dev server."
      );
      return;
    }
    try {
      msLoginHandledRef.current = false;
      sessionStorage.setItem(MS_LOGIN_INITIATED_KEY, "1");
      await instance.loginRedirect(microsoftLoginRedirectRequest);
    } catch (err) {
      notifyError(err?.message || "Microsoft sign-in failed");
      sessionStorage.removeItem(MS_LOGIN_INITIATED_KEY);
    }
  };

  useEffect(() => {
    if (!pendingApproval || pendingUserEmail || !accounts[0]) return;
    const userEmail = getAccountEmail(accounts[0]);
    if (userEmail) {
      const normalizedEmail = userEmail.toLowerCase();
      sessionStorage.setItem(MS_PENDING_USER_EMAIL, normalizedEmail);
      localStorage.setItem(LS_PENDING_USER_EMAIL, normalizedEmail);
      setPendingUserEmail(normalizedEmail);
    }
  }, [pendingApproval, pendingUserEmail, accounts]);

  const onRefreshPending = () => {
    window.location.reload();
  };

  const onExitPending = async () => {
    const email =
      pendingUserEmail || getStoredPendingEmail();
    clearPendingStorage(email);
    msLoginHandledRef.current = false;
    setPendingApproval(false);
    setPendingUserEmail(null);
    setPendingAuthToken(null);
    setRequestSent(false);
    try {
      if (typeof instance.clearCache === "function") {
        await instance.clearCache();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onRequestSent = () => {
    const email = pendingUserEmail || getStoredPendingEmail();
    markRequestSent(email);
    setRequestSent(true);
    setPendingApproval(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params?.get("token");
    if (token) {
      setToken(token);
    }
  }, []);

 
//I have optional param with token and if token is not null then show SetNewPassword component
if (token !== null && token !== undefined && token !== "" && token !== "null") {
  return (
    <SetNewPassword token={token} />
  )
}

if (showForgotPassword) {
  return (
    <ForgotPassword
      initialEmail={email}
      onBack={() => setShowForgotPassword(false)}
    />
  );
}

if (pendingApproval && !requestSent && pendingUserEmail && pendingAuthToken) {
  return (
    <SsoOnboarding
      userEmail={pendingUserEmail}
      authToken={pendingAuthToken}
      onRequestSent={onRequestSent}
      onExit={onExitPending}
    />
  );
}

if (pendingApproval) {
  return (
    <div className={`app-background w-full min-h-screen h-full flex items-center justify-center overflow-y-auto ${styles.loginPage}`}>
      <div className="app-background-surface w-full max-w-md px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8">
        <div className={styles.authForm}>
          <div className={styles.authHeader}>
            <div className={styles.authLogoWrap}>
              <img src={LOGO_SRC} alt="Company logo" className={styles.authLogo} />
            </div>
            <p className={styles.authSubtitle}>Account pending</p>
            <hr className={styles.authDivider} />
          </div>
          <div className={styles.authPendingNote}>
            Your request has been sent to the agency administrator. Please wait
            until they activate your account. After activation, refresh the
            page.
          </div>
          <button
            type="button"
            onClick={onRefreshPending}
            className={styles.authSubmit}
          >
            Refresh page
          </button>
          <button
            type="button"
            onClick={onExitPending}
            className={styles.authMicrosoft}
          >
            Back to login
          </button>
          <p className={styles.authFooterNote}>
            Secure login powered by enterprise-grade encryption
          </p>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className={`app-background w-full min-h-screen h-full flex items-center justify-center overflow-y-auto ${styles.loginPage}`}>
      <div className="app-background-surface w-full max-w-md px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8">
        {/* Logo */}
        

        {/* Login Card — colors match dashboard / app shell */}
        <div className={styles.authCard}>
          {/* Tabs - commented out
          <div className="flex border-b border-[rgba(255,255,255,0.06)]">
            <button
              onClick={() => setActiveTab("super-admin")}
              type="button"
              className={`flex-1 px-2 py-3 sm:px-4 sm:py-4 md:px-6 text-xs sm:text-sm font-medium transition-all whitespace-nowrap min-w-0
                ${activeTab === "super-admin"
                  ? "text-[#f1f5f9] bg-[rgba(255,255,255,0.06)] border-b-2 border-[#ef4444]"
                  : "text-[#6b7a90] hover:text-[#b0b8c8] hover:bg-[rgba(255,255,255,0.04)]"
                }`}
              style={{ marginBottom: 0, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, letterSpacing: '0.01em' }}
            >
              <span className="hidden sm:inline">Login as </span>Super Admin
            </button>
            <button
              onClick={() => setActiveTab("agency-admin")}
              type="button"
              className={`flex-1 px-2 py-3 sm:px-4 sm:py-4 md:px-6 text-xs sm:text-sm font-medium transition-all whitespace-nowrap min-w-0
                ${activeTab === "agency-admin"
                  ? "text-[#f1f5f9] bg-[rgba(255,255,255,0.06)] border-b-2 border-[#ef4444]"
                  : "text-[#6b7a90] hover:text-[#b0b8c8] hover:bg-[rgba(255,255,255,0.04)]"
                }`}
              style={{ marginBottom: 0, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, letterSpacing: '0.01em' }}
            >
              <span className="hidden sm:inline">Login as </span>Agency Admin
            </button>
          </div>
          */}
         

          {/* Login Form */}
          <form onSubmit={handleLogin} className={styles.authForm}>
          <div className={styles.authHeader}>
          <div className={styles.authLogoWrap}>
            <img src={LOGO_SRC} alt="Company logo" className={styles.authLogo} />
          </div>
          <p className={styles.authSubtitle}>Sign in to access your account</p>
          <hr className={styles.authDivider} />
        </div>
            {/* Email Input */}
            <div>
              <label htmlFor="email" className={styles.authLabel}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail className={styles.authIcon} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, pointerEvents: 'none' }} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className={`${styles.authInput} ${styles.authInputIconLeft}`}
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className={styles.authLabel}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock className={styles.authIcon} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, pointerEvents: 'none' }} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className={`${styles.authInput} ${styles.authInputIconBoth}`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className={styles.authToggle}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              <div className={styles.authForgotWrap}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className={styles.authForgotLink}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className={styles.authSubmit}>
              Login
            </button>

            {/* Divider */}
            {/* <div className={styles.authOrDivider} role="separator" aria-label="or">
              <span className={styles.authOrLine} aria-hidden="true" />
              <span className={styles.authOrText}>or</span>
              <span className={styles.authOrLine} aria-hidden="true" />
            </div> */}

            {/* Microsoft Login */}
            {/* <button
              type="button"
              onClick={onMicrosoftLogin}
              disabled={!isMsReady}
              className={styles.authMicrosoft}
            >
              <svg
                className={styles.authMicrosoftIcon}
                width="18"
                height="18"
                viewBox="0 0 21 21"
                aria-hidden="true"
              >
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Sign in with Microsoft
            </button> */}
          </form>
        </div>

        {/* Footer Info */}
        <p className={styles.authFooterNote}>
          Secure login powered by enterprise-grade encryption
        </p>
      </div>
    </div>
  );
};

export default Login;
